import {
  asAccruedInterestRate,
  asAmount,
  asBondPriceNominal,
  asCorporateBondId,
  asGSpread,
  asInterpolatedCDRate,
  asIssuerId,
  asYield,
} from "@goodfill/domain-properties";
import {
  zCouponPayment,
  zDecimal,
  zISODateTime,
  zMakeWholeCallEmptyStringToNull,
  zMoney,
  zRequiredString,
} from "@goodfill/domain-properties-zod-validators";
import { z } from "zod";
import { Fetcher } from "../../fetcher";
import { zBondRatings } from "../traderUi/bonds/zodBondRatings";
import { CognitoIdToken } from "../users/authTypes";

const bondSchema = z.object({
  // Ids
  bondIdCusip: zRequiredString().transform(asCorporateBondId),
  isin: zRequiredString(),

  // Bond Properties
  minimumPiece: zMoney(),
  couponPayment: zCouponPayment(),
  industry: zRequiredString(),
  maturityDate: zISODateTime(),
  esgScore: zRequiredString().transform(Number),
  paymentRank: zRequiredString(),
  ratings: zBondRatings,
  makeWholeCall: zMakeWholeCallEmptyStringToNull(),
  minimumIncrement: zMoney(),
  outstandingAmount: zMoney(),
  interpolatedCDRate: zDecimal().transform(asInterpolatedCDRate),
  mostRecentCouponPaymentDate: zISODateTime(),

  // Issuer
  issuerName: zRequiredString(),
  issuerTicker: zRequiredString(),
  issuerId: zRequiredString().transform(asIssuerId),

  // bid (customer sell)
  bidPrice: zDecimal().transform(asBondPriceNominal),
  bidYield: zDecimal().transform(asYield),
  bidAmount: zDecimal().transform(asAmount),
  bidPriceTimestamp: zISODateTime(),
  bidYieldTimestamp: zISODateTime(),
  bidGSpread: z.preprocess((a) => (a === "NaN" ? "0" : a), zDecimal().transform(asGSpread)),

  sellOfferGSpread: z.preprocess((a) => (a === "NaN" ? "0" : a), zDecimal().transform(asGSpread)), // calculated for 10k face amount, includes Goodfill commission
  sellOfferPrice: zDecimal().transform(asBondPriceNominal), // calculated for 10k face amount, includes Goodfill commission
  sellOfferYield: zDecimal().transform(asYield), // calculated for 10k face amount, includes Goodfill commission

  // ask (customer buy)
  askPrice: zDecimal().transform(asBondPriceNominal),
  askYield: zDecimal().transform(asYield),
  askAmount: zDecimal().transform(asAmount),
  askPriceTimestamp: zISODateTime(),
  askYieldTimestamp: zISODateTime(),
  askGSpread: z.preprocess((a) => (a === "NaN" ? "0" : a), zDecimal().transform(asGSpread)),

  buyOfferGSpread: zDecimal().transform(asGSpread), // calculated for 10k face amount, includes Goodfill commission
  buyOfferPrice: zDecimal().transform(asBondPriceNominal), // calculated for 10k face amount, includes Goodfill commission
  buyOfferYield: zDecimal().transform(asYield), // calculated for 10k face amount, includes Goodfill commission

  // Accrued
  accruedInterestRate: zDecimal().transform(asAccruedInterestRate),
  accruedDays: z.number(),

  // Extra
  isOfferedToCustomers: z.boolean(),
  isMarketDataFresh: z.boolean(),
  numberOfHoldingCustomers: z.number(),
  createdAt: zISODateTime(),
  updatedAt: zISODateTime(),
});
export type CorporateBondApi = z.infer<typeof bondSchema>;

const schema = z.array(bondSchema);

export type ApiListCompleteCorporateBondDetailsOkResponse = z.infer<typeof schema>;

export const listCompleteCorporateBondDetails = (fetcher: Fetcher, cognitoIdToken: CognitoIdToken) => {
  return fetcher({
    endpoint: "/bonds/completeCorporateBondDetails/list",
    method: "GET",
    schema,
    cognitoIdToken,
  });
};
